import dayjs from 'dayjs';

import { formatCurrency, formatNumber, formatScore, formatPrice } from '@/lib/format';
import type { Signal } from '@/types';

interface SignalBadgeProps {
	signal: Signal;
	minAtrPct?: number;
}

export function SignalBadge({ signal, minAtrPct }: SignalBadgeProps) {
	const typeColors = {
		ENTRY: 'bg-success/10 text-success border-success/20',
		EXIT: 'bg-danger/10 text-danger border-danger/20',
		STOP_LOSS: 'bg-danger/20 text-danger border-danger/30',
		TAKE_PROFIT: 'bg-success/20 text-success border-success/30',
		ADJUSTMENT: 'bg-info/10 text-info border-info/20',
		HOLD: 'bg-warning/10 text-warning border-warning/20',
		NO_ACTION: 'bg-text-muted/10 text-text-muted border-text-muted/20',
		TRAILING_STOP: 'bg-danger/20 text-danger border-danger/30',
		BLOCKED: 'bg-warning/20 text-warning border-warning/30'
	};

	const directionColors = {
		LONG: 'text-success',
		SHORT: 'text-danger'
	};

	const formatTime = (timestamp: number) => {
		return dayjs(timestamp).format('lll');
	};

	const getScoreColor = (score: number) => {
		if (score > 0) return 'text-success';
		if (score < 0) return 'text-danger';
		return 'text-text-muted';
	};

	const indicators = signal.indicators;
	const exitIndicators = signal.exitIndicators;

	// Exit score is composed as: exitTaScore + appliedProfit + appliedTimeDecay.
	// For shorts the raw profit/time-decay scores are negated when applied.
	const isLong = signal.direction === 'LONG';
	const appliedProfit =
		signal.profitScore !== undefined
			? isLong
				? signal.profitScore
				: -signal.profitScore
			: undefined;
	const appliedTimeDecay =
		signal.timeDecayScore !== undefined
			? isLong
				? signal.timeDecayScore
				: -signal.timeDecayScore
			: undefined;

	return (
		<div className="card border-l-4 border-l-primary">
			{/* Top Row: Type + Direction | Individual Scores */}
			<div className="flex items-center justify-between mb-2">
				<div className="flex items-center gap-2 flex-wrap">
					{signal.type === 'ADJUSTMENT' && signal.action ? (
						<span
							className={`px-2 py-1 rounded text-xs font-medium border ${typeColors[signal.type]}`}
						>
							{signal.action} {signal.direction}
						</span>
					) : (
						<>
							<span
								className={`px-2 py-1 rounded text-xs font-medium border ${typeColors[signal.type]}`}
							>
								{signal.type}
							</span>
							{signal.triggerSource && (
								<span
									className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
										signal.triggerSource === 'atr'
											? 'bg-primary/20 text-primary border border-primary/30'
											: 'bg-surface text-text-muted border border-border'
									}`}
								>
									{signal.triggerSource}
								</span>
							)}
							<span className={`text-sm font-medium ${directionColors[signal.direction]}`}>
								{signal.direction}
							</span>
						</>
					)}
				</div>

				{/* Individual Scores */}
				{(indicators || exitIndicators) && (
					<div className="flex flex-col items-end gap-1 text-xs">
						{indicators && (
							<div className="flex items-center gap-2">
								<span className="text-text-muted">Entry</span>
								<span className={getScoreColor(indicators.vwap)}>
									VWAP {formatScore(indicators.vwap, 2)}
								</span>
								<span className={getScoreColor(indicators.bbands)}>
									BB {formatScore(indicators.bbands, 2)}
								</span>
								<span className={getScoreColor(indicators.rsi)}>
									RSI {formatScore(indicators.rsi, 2)}
								</span>
								<span className={getScoreColor(indicators.obv)}>
									OBV {formatScore(indicators.obv, 2)}
								</span>
							</div>
						)}
						{exitIndicators && (
							<div className="flex items-center gap-2">
								<span className="text-text-muted">Exit</span>
								<span className={getScoreColor(exitIndicators.vwap)}>
									VWAP {formatScore(exitIndicators.vwap, 2)}
								</span>
								<span className={getScoreColor(exitIndicators.bbands)}>
									BB {formatScore(exitIndicators.bbands, 2)}
								</span>
								<span className={getScoreColor(exitIndicators.rsi)}>
									RSI {formatScore(exitIndicators.rsi, 2)}
								</span>
								<span className={getScoreColor(exitIndicators.obv)}>
									OBV {formatScore(exitIndicators.obv, 2)}
								</span>
							</div>
						)}
					</div>
				)}
			</div>

			{/* Info Row: Score + Price + Threshold + ATR% + Reason */}
			<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-2">
				<span className="text-text-muted">
					Entry Score:{' '}
					<span className={`font-medium ${getScoreColor(signal.taScore)}`}>
						{formatScore(signal.taScore)}
					</span>
				</span>
				{signal.exitScore !== undefined && (
					<span className="text-text-muted">
						Exit Score:{' '}
						<span className={`font-medium ${getScoreColor(signal.exitScore)}`}>
							{formatScore(signal.exitScore)}
						</span>
					</span>
				)}
				<span className="text-text-muted">
					Price: <span className="text-text">{formatPrice(signal.price)}</span>
				</span>
				<span className="text-text-muted">
					Threshold:{' '}
					<span className={getScoreColor(signal.threshold)}>{formatScore(signal.threshold)}</span>
				</span>
				{signal.atr !== undefined && signal.atr > 0 && (
					<span className="text-text-muted">
						ATR%:{' '}
						<span
							className={
								minAtrPct !== undefined && signal.atr / signal.price < minAtrPct
									? 'text-warning font-medium'
									: 'text-text font-medium'
							}
						>
							{((signal.atr / signal.price) * 100).toFixed(2)}%
						</span>
						{minAtrPct !== undefined && (
							<span className="text-xs text-text-muted ml-1">
								(min {(minAtrPct * 100).toFixed(2)}%)
							</span>
						)}
					</span>
				)}
				{signal.reason && signal.reason.trim() !== '' && (
					<span className="text-text-muted">
						Reason:{' '}
						<span
							className={
								signal.reason === 'MIN_ATR_PCT' ||
								signal.reason === 'TRADING_PAUSED' ||
								signal.reason === 'CIRCUIT_BREAKER'
									? 'text-warning font-medium'
									: 'text-text'
							}
						>
							{signal.reason}
						</span>
					</span>
				)}
			</div>

			{/* Position Info Row */}
			<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-2">
				{signal.positionSize !== undefined && signal.positionSize !== 0 && (
					<span className="text-text-muted">
						Size: <span className="text-text">{formatCurrency(signal.positionSize, 2)}</span>
					</span>
				)}
				{signal.entryPrice !== undefined && signal.entryPrice !== 0 && (
					<span className="text-text-muted">
						Entry: <span className="text-text">{formatPrice(signal.entryPrice)}</span>
					</span>
				)}
				{/* Adjustment details */}
				{signal.type === 'ADJUSTMENT' && signal.currentSize !== undefined && (
					<span className="text-text-muted">
						Current: <span className="text-text">{formatNumber(signal.currentSize, 2)}</span>
					</span>
				)}
				{signal.type === 'ADJUSTMENT' && signal.targetSize !== undefined && (
					<span className="text-text-muted">
						Target: <span className="text-text">{formatNumber(signal.targetSize, 2)}</span>
					</span>
				)}
				{signal.intensity !== undefined && (
					<span className="text-text-muted">
						Intensity: <span className="text-text">{formatNumber(signal.intensity, 2)}</span>
					</span>
				)}
			</div>

			{/* PnL Row for exit signals - unrealized becomes realized when position is closed */}
			{(signal.type === 'EXIT' || signal.type === 'STOP_LOSS' || signal.type === 'TAKE_PROFIT') &&
				(signal.unrealizedPnl !== undefined || signal.realizedPnl !== undefined) && (
					<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-2">
						{/* For closed positions, show the final PnL as Realized (the unrealized value at close becomes realized) */}
						{signal.unrealizedPnl !== undefined && (
							<span className="text-text-muted">
								PnL:{' '}
								<span className={signal.unrealizedPnl >= 0 ? 'text-success' : 'text-danger'}>
									{formatCurrency(signal.unrealizedPnl, 2)}
								</span>
							</span>
						)}
					</div>
				)}

			{/* Exit Score composition (HOLD / signal-based EXIT) */}
			{signal.exitScore !== undefined && signal.exitTaScore !== undefined ? (
				<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-2">
					<span className="text-text-muted">
						Exit TA:{' '}
						<span className={getScoreColor(signal.exitTaScore)}>
							{formatScore(signal.exitTaScore)}
						</span>
					</span>
					{appliedProfit !== undefined && (
						<span className="text-text-muted">
							Profit adj:{' '}
							<span className={getScoreColor(appliedProfit)}>{formatScore(appliedProfit)}</span>
						</span>
					)}
					{appliedTimeDecay !== undefined && (
						<span className="text-text-muted">
							Time decay adj:{' '}
							<span className={getScoreColor(appliedTimeDecay)}>
								{formatScore(appliedTimeDecay)}
							</span>
						</span>
					)}
					<span className="text-text-muted text-xs">(included in Exit Score)</span>
				</div>
			) : (
				(signal.profitScore !== undefined || signal.timeDecayScore !== undefined) && (
					<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-2">
						{signal.profitScore !== undefined && (
							<span className="text-text-muted">
								Profit Score:{' '}
								<span className={getScoreColor(signal.profitScore)}>
									{formatScore(signal.profitScore)}
								</span>
							</span>
						)}
						{signal.timeDecayScore !== undefined && (
							<span className="text-text-muted">
								Time Decay:{' '}
								<span className={getScoreColor(signal.timeDecayScore)}>
									{formatScore(signal.timeDecayScore)}
								</span>
							</span>
						)}
					</div>
				)
			)}

			{/* Bottom: Timestamp */}
			<div className="text-xs text-text-muted">{formatTime(signal.timestamp)}</div>
		</div>
	);
}
